import queryString from 'query-string';
import { mapActions, mapGetters } from 'vuex';

import ContentType from '@/helpers/contentType';
import CategoryType from '@/helpers/contentCategory';
import { hasPermission } from '@/helpers/permission';
import { hasSomeEqualWebAccessabilityOptionActive } from '@/helpers/equalweb';
import ConditionalTypes from '@/helpers/availabilityConditionalTypes';
import { PROCESSING_STATUS } from '@/helpers/avaliaActivity.js';
import { SCORM_UPLOAD_STATUS } from '@/helpers/scorm';
import AcademicMainTypes from '@/helpers/academicMainType';
import ApprovalStatus from '@/helpers/approvalStatus';
import { AUTO_GRADE_STATUS } from '@/helpers/smartIntegrations';

import ValidateTopicConfigurations from '@/mixins/validateTopicConfigurations';
import StudentModeMixin from '@/mixins/student-mode';
import OpenNewTabMixin from '@/mixins/open-new-tab';

export default {
  mixins: [ValidateTopicConfigurations, StudentModeMixin, OpenNewTabMixin],
  data() {
    return {
      AUTO_GRADE_STATUS,
      searchModuleAndGroupModuleByName: ''
    };
  },
  computed: {
    ...mapGetters({
      getEmail                       : '_core/user/getEmail',
      getName                        : '_core/user/getName',
      getterUserRoleContext          : 'plataforma/user/getterUserContext',
      getterCourse                   : 'plataforma/newContent/getterCourse',
      getterAutoGradeStatus          : 'plataforma/newContent/getterAutoGradeStatus',
      getterContent                  : 'plataforma/newContent/getterContent',
      getterInstitutionConfiguration : 'plataforma/institutionConfiguration/getterInstitutionConfiguration',
      userPermissions                : 'plataforma/courses/userPermissions'
    }),
    getContext() {
      return this.$route.params.id ? this.getterContent.context : this.getterCourse.context;
    },
    getBanner() {
      if (this.getterCourse.context) {
        return this.getterCourse.context.academicMainPhotoUrl || require('~/assets/img/default-course-wallpaper.jpg');
      }

      return '';
    },
    computedTopics() {
      if (this.getterCourse.topics) {
        const topics = JSON.parse(JSON.stringify(this.getterCourse.topics));

        const returnedTopic = topics.map(topic => {
          if (this.includesInSearch(topic, this.searchModuleAndGroupModuleByName)) {
            return topic;
          } else if (topic.children && topic.children.filter(children => this.includesInSearch(children, this.searchModuleAndGroupModuleByName)).length) {
            return {
              ...topic,
              children: topic.children.filter(children => this.includesInSearch(children, this.searchModuleAndGroupModuleByName))
            };
          }
        }).filter(item => item);

        return returnedTopic;
      }

      return [];
    },
    hasConditionalsError() {
      if (!this.topic.hasConditional) {
        return false;
      }

      return this.hasConditionalError(this.topic);
    },
    hasMaxModulePermission() {
      return hasPermission(
        this.getterCourse.context?.academicMainId,
        this.getterCourse.context?.academicMainTypeId,
        this.getterCourse.context?.isMarketplace,
        this.getterCourse.context?.topicTypeId,
        "approval_flow_max_modules",
        this.userPermissions
      );
    },
    isMaxModuleApprovalFlow() {
      return this.getterCourse.context?.academicMainTypeId === AcademicMainTypes.MODEL && this.getterInstitutionConfiguration.useApprovalFlow && this.getterInstitutionConfiguration.useMaxModuleApprovalFlow;
    },
    countAllModules() {
      const totalModules = [];

      this.getterCourse.topics.forEach((topic) => {
        if (topic.topicTypeId === ContentType.PAGE) {
          totalModules.push(topic);
        };
      });

      this.getterCourse.topics.forEach((topic) => {
        topic.children?.forEach((child) => {
          if (child.topicTypeId === ContentType.PAGE) {
            totalModules.push(child);
          }
        });

      });

      return totalModules.length;
    },
    maxModuleAchievedValidation() {
      return this.isMaxModuleApprovalFlow && this.countAllModules === this.getterCourse.context.maxModule;
    },
    showPendingApprovalBanner() {
      const institutionUseApprovalFlow = this.getterInstitutionConfiguration.useApprovalFlow;
      const isModelAwaitingApproval = this.getterCourse.context.approvalFlowStatusId === ApprovalStatus.AWAITING_APPROVAL;

      return institutionUseApprovalFlow && isModelAwaitingApproval;
    },
    canImportContent() {
      const context = this.getContext;

      return hasPermission(
        context.academicMainId,
        context.academicMainTypeId,
        false,
        null,
        'import_content',
        this.userPermissions
      );
    },
    showImportContentButton() {
      const isCourseModel = this.getterCourse.context?.academicMainTypeId === AcademicMainTypes.MODEL;
      const institutionUseApprovalFlow = this.getterInstitutionConfiguration.useApprovalFlow;
      const isModelAwaitingApproval = this.getterCourse.context.approvalFlowStatusId === ApprovalStatus.AWAITING_APPROVAL;
      const isModelApproved = this.getterCourse.context.approvalFlowStatusId === ApprovalStatus.APPROVED;

      return institutionUseApprovalFlow && isCourseModel && (isModelAwaitingApproval || isModelApproved);
    }
  },
  methods: {
    ...mapActions({
      actionAddProgress       : 'plataforma/newContent/actionAddProgress',
      actionWebconferenceURL  : 'plataforma/newContent/actionWebconferenceURL',
      actionSSOUrl            : '_core/auth/actionSSOUrl',
      actionPatchApprovalFlow : 'plataforma/newContent/actionPatchApprovalFlow',
      actionSendContentToAi   : 'plataforma/newContent/actionSendContentToAi',
      isImpersonated          : '_core/auth/isImpersonated'
    }),
    hasConditionalError(topic) {
      for (const conditional of topic.conditionals) {
        if (conditional.typeId === ConditionalTypes.DATE_ACADEMIC_MAIN && !this.getterCourse.context.academicMainStartAt) {
          return true;
        }
      }
      return false;
    },
    includesInSearch(topic, search) {
      return topic.title.toLowerCase().includes(search ? search.toLowerCase() : '');
    },
    isOrganizationAndHasNoChild() {
      return this.topic.categoryTypeId === CategoryType.ORGANIZATION && (!this.topic.children || !this.topic.children.length);
    },
    welcomeText() {
      const hour = new Date().getHours();

      if (hour < 12) {
        return this.$t('content.layout.timeline.course.goodMorning');
      } else if (hour < 18) {
        return this.$t('content.layout.timeline.course.goodAfternoon');
      } else {
        return this.$t('content.layout.timeline.course.goodEvening');
      }
    },
    openWebConference(topic) {
      this.$nuxt.$loading.start();

      const data = {
        academicMainId : this.getterCourse.context.academicMainId,
        topicId        : topic.id,
        email          : this.getEmail,
        name           : this.getName
      };

      this.actionWebconferenceURL(data)
        .then(url => {
          setTimeout(() => {
            window.open(url, '_blank');
          });
        })
        .catch(() => {
          this.$toast.error(this.$t('contentType.webconference.linkGeneratorError'));
        })
        .finally(() => {
          this.$nuxt.$loading.finish();
        });
    },
    markProgress(topic) {
      this.actionAddProgress({
        academicMainId : this.$route.params.courseId,
        topicId        : topic.id
      })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    sendTracking() {
      this.$services.contentV2().getTrackerInfo({
        academicMainId : this.getContext.academicMainId,
        topicType      : this.topic.categoryTypeId,
        topicId        : this.topic.id
      }).catch(() => {
        this.$toast.error(this.$t('content.errorTrackingLog'));
        return;
      });
    },
    callAvaliaSSO(queryParams, topic) {
      this.$nuxt.$loading.start();
      this.$services.contentV2().validateAvaliaActivityAccess({
        accessCode      : queryParams.accessCode,
        destinationPage : queryParams.destinationPage,
        academicMainId  : this.$route.params.courseId,
        topicId         : topic.id
      })
        .then(result => {
          delete queryParams.accessCode;
          const queryStringFormat = queryString.stringify({ ...queryParams, ...this.$route.query }, { skipNull: true, skipEmptyString: true });
          const urlAvalia = `${result.data.url}&${queryStringFormat}&returnUrl=/sso`;

          this.$nuxt.$emit('isOpenContentModalAvalia', {
            urlAvalia,
            queryParams,
            title                 : topic.title,
            academicMainId        : this.$route.params.courseId,
            topicId               : topic.id,
            hasRetries            : topic.content.hasRetries,
            numberRetries         : topic.content.numberRetries,
            studentCurrentAttempt : topic.content.studentCurrentAttempt
          });

          this.$nuxt.$emit('changeIsOpenModalAccessCodeValidator', { isAccessCodeModalOpen: false });
        })
        .catch(error => {
          if (error.response.data.error.code === 'plataforma-v2-content-svc-401_topic-access_code_invalid') {
            this.$nuxt.$emit('avaliaTopicAccessCodeInvalid');
            return;
          }

          this.$toast.error(error.response.data.error.message);
        })
        .finally(() => {
          this.$nuxt.$loading.finish();
        });
    },
    openTopic(topic, topicId) {
      const event = new CustomEvent('myOpenTopicEvent', {
        detail: {
          topicId: topic.id
        }
      });
      window.dispatchEvent(event);

      switch (topic.categoryTypeId) {
        case CategoryType.FORUM:
          this.$nuxt.$emit('openForumView', {
            academicMainId : this.$route.params.courseId,
            topicId        : topic.id
          });
          break;
        case CategoryType.LTI: {
          let routeLTI = '';

          if (topic.content.url.toLowerCase().startsWith(process.env.S3_CDN_URL.toLowerCase())) {
            routeLTI = topic.content.url;
          } else {
            routeLTI = `lti-launch/?type=lti&id=${topic.id}${!topic.content.isForOpenInNewTab && this.isStudentMode ? '&hideStudentModeFooter=true' : ''}`;
          }

          if (topic.topicTypeId === ContentType.LTI && (topic.content.isForOpenInNewTab || hasSomeEqualWebAccessabilityOptionActive())) {
            this.openUrlInNewTab(routeLTI);
          } else {
            this.$nuxt.$emit('isOpenContentModalLti', {
              link  : routeLTI,
              title : topic.title
            });
          }

          if (!this.isImpersonated) {
            this.markProgress(topic);
          }
          break;
        }
        case CategoryType.MENU_INTERACTIVES: {
          if (hasSomeEqualWebAccessabilityOptionActive()) {
            window.open(topic.content.url, '_blank');
          } else {
            this.$nuxt.$emit('isOpenContentModalLti', {
              link              : topic.content.url,
              title             : topic.title,
              accessibilityText : topic.content.accessibilityText
            });
          }

          this.markProgress(topic);
          break;
        }
        case CategoryType.H5P: {
          if (topic.content) {
            this.$nuxt.$emit('isOpenContentModalH5p', { topic });
          }
          break;
        }
        case CategoryType.SCORM: {
          if (topic.content.uploadStatus === SCORM_UPLOAD_STATUS.ERROR) {
            this.editTopic(topic);
            return;
          } else if (topic.content.uploadStatus !== SCORM_UPLOAD_STATUS.COMPLETE) {
            return;
          }

          if (topic.content) {
            this.$nuxt.$emit('isOpenContentModalScorm', {
              topicId        : topic.id,
              title          : topic.title,
              content        : topic.content,
              progressTypeId : topic.progressTypeId
            });
          }
          break;
        }
        case CategoryType.WEBCONFERENCE:
          if (!this.getterUserRoleContext.isStudent || (!topic.content.hasEnded && topic.content.allowJoinWebconference)) {
            this.openWebConference(topic);
            this.markProgress(topic);
          }

          break;
        case CategoryType.AVALIA:
          if (
            topic.isFuture ||
            !topic.content.isActive ||
            topic.content.status !== PROCESSING_STATUS.OK ||
            (!this.topicHasRetries(topic) && !this.isTopicExpired(topic) && this.isTopicFinished(topic) && !this.shouldShowResultActivity(topic)) ||
            (this.topicHasRetries(topic) && (this.studentAnsweredAllAttempts(topic) && !this.shouldShowResultActivity(topic))) ||
            (this.isTopicExpired(topic) && !this.shouldShowResultActivity(topic))
          ) {
            return;
          }

          if (this.getterUserRoleContext.isStudent) {
            if (topic.isAccessCodeEnabled && this.topicIsAvailableToAnswer(topic) && !this.isCompleted(topic)) {
              this.$nuxt.$emit('changeIsOpenModalAccessCodeValidator', {
                topic,
                academicMainId: this.$route.params.courseId
              });

              this.$nuxt.$on('avaliaTopicAccessCodeApproved', params => {
                this.mountAvaliaParamsAndOpenmodal(params.topic, params.accessCode);
              });
              return;
            }

            this.mountAvaliaParamsAndOpenmodal(topic);
          }

          break;
        default:
          this.$router.push({ path: `/course/${this.$route.params.courseId}/content/${topic.id}`, query: { id: topicId } });
          break;
      }
    },
    mountAvaliaParamsAndOpenmodal(topic, accessCode = null) {
      let queryParams = {};
      if (!this.isTopicExpired(topic) && !topic.isFuture && ((!this.topicHasRetries(topic) && !this.isTopicFinished(topic)) || (this.topicHasRetries(topic) && !this.studentAnsweredAllAttempts(topic)))) {
        queryParams = {
          destinationPage                  : null,
          classExternalId                  : this.getterCourse.context.academicMainExternalId,
          activityExternalSource           : topic.content.activityExternalSource,
          questionCollectionExternalSource : topic.content.questionCollectionExternalSource,
          activityExternalId               : topic.content.activityExternalId,
          questionCollectionExternalId     : topic.content.questionCollectionExternalId,
          routeQuery                       : JSON.stringify(this.$route.query),
          accessCode
        };
      } else {
        queryParams = {
          destinationPage                  : 'results',
          classExternalId                  : this.getterCourse.context.academicMainExternalId,
          activityExternalSource           : topic.content.activityExternalSource,
          questionCollectionExternalSource : topic.content.questionCollectionExternalSource,
          activityExternalId               : topic.content.activityExternalId,
          questionCollectionExternalId     : topic.content.questionCollectionExternalId,
          routeQuery                       : JSON.stringify(this.$route.query),
          accessCode                       : null
        };
      }

      this.callAvaliaSSO(queryParams, topic);
    },
    handleTopicClick({ bypassCommonConditionalRules } = { bypassCommonConditionalRules: false }) {
      if ([CategoryType.MENU_INTERACTIVES, CategoryType.AVALIA, CategoryType.LTI].includes(this.topic.categoryTypeId)) {
        this.sendTracking();
      }

      if (!this.isStudent) {
        this.$emit('click');
        return;
      }

      const preventClickByConditionalRules = bypassCommonConditionalRules ? false : (this.isBlockedByParentConditional || this.topic.isBlockedContentByConditional);
      if (this.isOrganizationAndHasNoChild() || preventClickByConditionalRules) {
        return;
      }

      this.$emit('click');
    },
    isFeatureLocked(feature) {
      return process.env.FEATURES_LOCK?.includes(feature);
    },
    createModule(topic = false) {
      const params = {
        parentTopicId      : topic ? topic.id : null,
        academicMainTypeId : this.getterCourse.context.academicMainTypeId,
        version            : this.getterCourse.context.version,
        academicMainId     : this.getterCourse.context.academicMainId
      };

      this.$nuxt.$emit('changeIsOpenModalContentPage', params);
    },
    emitCreateContentOrExercise(topic) {
      const params = {
        academicMainId     : this.getContext.academicMainId,
        academicMainTypeId : this.getContext.academicMainTypeId,
        parentTopic        : topic,
        version            : this.getContext.version,
        isMarketplace      : false
      };

      this.$nuxt.$emit('changeIsOpenModalContentTypes', params);
    },
    haveAliasPermissionOnCourse(alias) {
      if (!this.getterCourse.context) {
        return false;
      };

      return hasPermission(
        this.getterCourse.context.academicMainId,
        this.getterCourse.context.academicMainTypeId,
        false,
        null,
        alias,
        this.getterCourse.permissions
      );
    },
    changeMaxModuleValue(maxModule) {
      this.$nuxt.$loading.start();

      const params = {
        academicMainId : this.$route.params.courseId,
        data           : { maxModule }
      };

      this.actionPatchApprovalFlow(params)
        .then(() => {
          this.$toast.success(this.$t('approvalFlow.successMessagesSnackbar.successMessage'));
        }).catch((err) => {
          this.$toast.error(err.data.error.message);
        }).finally(() => {
          this.$nuxt.$loading.finish();
        });
    },
    getSummaryText() {
      if (this.isMaxModuleApprovalFlow) {
        return this.$t('approvalFlow.summary.title');
      }

      return this.getterUserRoleContext.isStudent ? this.$t('content.layout.timeline.course.classSummaryStudent') : this.$t('content.layout.timeline.course.classSummary');
    },
    maxModuleAchievedModal() {
      this.$dialog.show({
        type                 : 'info',
        title                : this.$t('content.layout.timeline.course.approvalFlow.maxModuleInfoModalTitle'),
        message              : this.$t('content.layout.timeline.course.approvalFlow.maxModuleInfoModalMessage'),
        showCancelButton     : false,
        confirmButtonVariant : 'text'
      });
    },
    adjustMaxModuleLimit(approvalFlowStatus) {
      if (approvalFlowStatus === ApprovalStatus.AWAITING_APPROVAL || approvalFlowStatus === ApprovalStatus.APPROVED) {
        this.$dialog.show({
          type                 : 'info',
          title                : this.$t('content.layout.timeline.course.approvalFlow.maxModule.AdjustLimitInfo.title'),
          message              : this.$t('content.layout.timeline.course.approvalFlow.maxModule.AdjustLimitInfo.description'),
          showCancelButton     : false,
          confirmButtonVariant : 'text'
        });

        return false;
      }

      return true;
    },
    approveCourse() {
      this.$dialog.show({
        type              : 'warning',
        title             : this.$t('approvalFlow.approveModelCourse.confirmApprovalDialog.title'),
        message           : this.$t('approvalFlow.approveModelCourse.confirmApprovalDialog.message'),
        confirmButtonText : this.$t('approvalFlow.approveModelCourse.confirmApprovalDialog.confirmBtnText'),
        onConfirm         : ({ done, startLoading, stopLoading }) => {
          const params = {
            academicMainId : this.$route.params.courseId,
            data           : { approvalFlowStatusId: ApprovalStatus.APPROVED }
          };

          startLoading();

          this.actionPatchApprovalFlow(params)
            .then(() => {
              this.$toast.success(this.$t('approvalFlow.approveModelCourse.confirmApprovalDialog.approvalSuccessMessage'));
              done();
            })
            .catch((err) => {
              this.$toast.error(err.data.error.message);
            })
            .finally(() => {
              stopLoading();
            });
        }
      });
    },
    rejectCourse() {
      // ação de rejeitar ficou na modal pois precisamos do motivo de rejeição e não conseguimos esse valor pelo onConfirm
      // components/content-v2/modals/reject-approval-modal
      this.$dialog.show({
        type    : 'warning',
        title   : this.$t('approvalFlow.approveModelCourse.confirmRejectDialog.title'),
        message : this.$t('approvalFlow.approveModelCourse.confirmRejectDialog.message'),
        target  : 'reject-approval-dialog'
      });
    },
    sendContentToAI() {
      this.$nuxt.$loading.start();

      this.actionSendContentToAi({
        academicMainId: this.getterCourse.context.academicMainId
      })
        .then(() => {
          this.$toast.success(this.$t('content.layout.timeline.course.smartIntegrations.autoGrade.contentSent'));
        })
        .catch(() => {
          this.$toast.error(this.$t('content.layout.timeline.course.smartIntegrations.autoGrade.contentSentError'));
        }).finally(() => {
          this.$nuxt.$loading.finish();
        });
    }
  }
};
